import React, { useMemo, useState } from "react";
import { galleryItems } from "../data/gallery";

const byPriorityAsc = (a, b) => {
  // Step 1: Priority
  const pa = a.priority ?? "low";
  const pb = b.priority ?? "low";

  if (pa === "high" && pb !== "high") return -1;
  if (pb === "high" && pa !== "high") return 1;
};

const pill =
  "flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-xl bg-[#e7f3ef] pl-4 pr-2";

export default function Gallery() {
  const [selected, setSelected] = useState("All");
  const [enlargedIndex, setEnlargedIndex] = useState(null);
  const sortedGalleryItems = [...galleryItems].sort(byPriorityAsc);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(sortedGalleryItems.map((g) => g.category)));
    return ["All", ...unique];
  }, []);

  const items = useMemo(
    () =>
      selected === "All"
        ? sortedGalleryItems
        : sortedGalleryItems.filter((g) => g.category === selected),
    [selected]
  );

  const handlePrev = () => {
    if (enlargedIndex !== null) {
      setEnlargedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
    }
  };

  const handleNext = () => {
    if (enlargedIndex !== null) {
      setEnlargedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
    }
  };

  return (
    <div className="px-4 lg:px-40 md:px-20 flex flex-1 justify-center py-5">
      <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
        <div className="flex flex-wrap justify-between gap-3 p-4">
          <div className="flex min-w-72 flex-col gap-3">
            <p className="text-[#0e1b17] text-[28px] md:text-[32px] font-bold leading-tight">
              Our Gallery
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 p-3 flex-wrap pr-4 text-[#0e1b17] text-sm font-medium">
          {categories.map((c) => (
            <button
              key={c}
              className={`${pill} ${
                selected === c ? "outline outline-2 outline-[#14b881]" : ""
              }`}
              onClick={() => setSelected(c)}
            >
            {c}
            <p className=""></p>
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="relative">
          <div className="grid-auto-cards p-4">
            {items.map((it, idx) => (
              <div key={it.id} className="flex flex-col gap-3">
                <div
                  className="w-full aspect-square rounded-xl overflow-hidden cursor-pointer"
                  onClick={() => setEnlargedIndex(idx)}
                >
                  <img
                    src={it.image}
                    alt={it.category}
                    className="w-full h-full object-cover transition-transform duration-200 hover:scale-105"
                  />
                </div>
              </div>
            ))}
            {items.length === 0 && (
              <p className="p-4 text-sm text-[#4e977f]">No items found.</p>
            )}
          </div>

          {/* Enlarged Modal */}
          {enlargedIndex !== null && (
            <div className="modal-overlay"
              onClick={() => setEnlargedIndex(null)} // close on backdrop click
            >
              <div className="modal-box flex items-center justify-center"
                onClick={(e) => e.stopPropagation()} // prevent backdrop close
              >
                <img
                  src={items[enlargedIndex].image}
                  alt="Preview"
                  className="modal-img"
                />

                {/* Close */}
                <button className="modal-close"
                  onClick={() => setEnlargedIndex(null)}
                  aria-label="Close"
                >
                  ✕
                </button>

                {/* Left Button */}
                <button className="modal-nav left-3"
                  onClick={handlePrev}
                >
                  ‹
                </button>

                {/* Right Button */}
                <button className="modal-nav right-3"
                  onClick={handleNext}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
